import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import { LoadingSpinner } from '@components/atoms/LoadingSpinner';

// ============================================
// Protected Route Component
// ============================================

/**
 * ProtectedRoute - Beschermt routes die authenticatie vereisen.
 * 
 * Gebruik:
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/sessies" element={<SessiesPage />} />
 *   <Route path="/dashboard" element={<DashboardPage />} />
 * </Route>
 * ```
 * 
 * Of met children:
 * ```tsx
 * <ProtectedRoute>
 *   <SessiesPage />
 * </ProtectedRoute>
 * ```
 */

interface ProtectedRouteProps {
  children?: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute = ({
  children,
  redirectTo = '/login',
}: ProtectedRouteProps) => {
  const { isIngelogd, hydrated } = useAuthStore();

  // Wacht tot persist hydratie klaar is
  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect naar login als niet ingelogd
  if (!isIngelogd) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render children of Outlet (voor nested routes)
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
